<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="sideImg"
            alt="Phone Setup"
          />
        </div>
      </b-col>
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title
            title-tag="h2"
            class="font-weight-bold mb-1"
          >
            {{ $t("Confirm your phone number") }}
          </b-card-title>
          <b-card-text class="mb-2">
            {{ $t("A phone number is required on this account before you can continue.") }}
          </b-card-text>

          <b-alert
            v-if="errorMessage"
            show
            variant="danger"
          >
            {{ errorMessage }}
          </b-alert>

          <b-form
            v-if="step === 'phone'"
            class="mt-2"
            @submit.prevent="requestChallenge"
          >
            <b-form-group
              :label="$t('Phone Number')"
              label-for="setup-phone"
            >
              <b-form-input
                id="setup-phone"
                v-model="phone"
                :formatter="phoneFormatter"
                :state="!phone ? null : phoneValid"
                :placeholder="$t('Phone Number')"
              />
              <small
                v-if="phone && !phoneValid"
                class="text-danger"
              >{{ $t('Enter a valid mobile phone number') }}</small>
            </b-form-group>
            <b-button
              type="submit"
              variant="primary"
              block
              :disabled="!phoneValid || loading"
            >
              {{ $t('Send Verification Code') }}
            </b-button>
          </b-form>

          <b-form
            v-else
            class="mt-2"
            @submit.prevent="verify"
          >
            <b-form-group
              :label="$t('Verification Code')"
              label-for="setup-code"
            >
              <b-form-input
                id="setup-code"
                v-model="code"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
                :formatter="codeFormatter"
              />
            </b-form-group>
            <small
              v-if="expired"
              class="text-danger d-block"
            >
              {{ $t('The code has expired. Request a new one.') }}
            </small>
            <small
              v-else-if="expiresIn > 0"
              class="text-muted d-block"
            >
              {{ $t('The code expires in {seconds} seconds.', { seconds: expiresIn }) }}
            </small>
            <small
              v-if="cooldown > 0"
              class="text-muted d-block"
            >
              {{ $t('You can request a new code in {seconds} seconds.', { seconds: cooldown }) }}
            </small>
            <div class="d-flex justify-content-between mt-2">
              <b-button
                variant="outline-secondary"
                @click="backToPhone"
              >
                {{ $t('Change phone') }}
              </b-button>
              <span>
                <b-button
                  variant="link"
                  :disabled="loading || cooldown > 0 || restartRequired"
                  @click="requestChallenge"
                >
                  {{ $t('Resend Code') }}
                </b-button>
                <b-button
                  variant="primary"
                  :disabled="loading || expired || !codeValid || restartRequired"
                  @click="verify"
                >
                  {{ $t('Verify') }}
                </b-button>
              </span>
            </div>
          </b-form>
        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BImg, BCardTitle, BCardText, BAlert, BForm, BFormGroup, BFormInput, BButton,
} from 'bootstrap-vue'
import axios from '@axios'
import useJwt from '@/auth/jwt/useJwt'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatPhone, isValidPhone, stripPhone } from '@/libs/phone'
import { OTP_ERROR_KEYS, OTP_ERROR_KEYS_BY_CODE } from '@/libs/otpI18nKeys'
import {
  SOURCE_LOGIN_LEGACY,
  SOURCE_LOGIN_MODERN,
  SOURCE_PASSWORD,
  canStartPhoneSetupRequest,
  clearPhoneSetupHandoff,
  getValidPhoneSetupHandoff,
} from '@/auth/phoneSetupHandoff'
import sideImg from '@/assets/images/pages/login-v2.svg'

/* eslint-disable import/extensions, import/no-unresolved, import/no-absolute-path */
const { axiosRoutes: { auth: authRoutes } } = require('/G_CONFIG.js')
/* eslint-enable import/extensions, import/no-unresolved, import/no-absolute-path */

export default {
  components: {
    BRow, BCol, BImg, BCardTitle, BCardText, BAlert, BForm, BFormGroup, BFormInput, BButton,
  },
  data() {
    return {
      handoff: null,
      step: 'phone',
      phone: '',
      challengeId: '',
      code: '',
      cooldown: 0,
      expiresIn: 0,
      expired: false,
      loading: false,
      errorMessage: '',
      restartRequired: false,
      timer: null,
      expiryTimer: null,
      sideImg,
    }
  },
  computed: {
    phoneValid() {
      return isValidPhone(this.phone)
    },
    codeValid() {
      return /^\d{6}$/.test(this.code)
    },
  },
  created() {
    this.handoff = getValidPhoneSetupHandoff()
    if (!this.handoff) this.exitInvalidHandoff()
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
    if (this.expiryTimer) clearInterval(this.expiryTimer)
  },
  methods: {
    phoneFormatter: formatPhone,
    codeFormatter(value) {
      return String(value == null ? '' : value).replace(/\D/g, '').slice(0, 6)
    },
    authHeaders() {
      return {
        timeout: 30000,
        skipAuthToken: true,
        skipAuthRefresh: true,
        skipAuthErrorHandling: true,
        headers: { Authorization: `Bearer ${this.handoff.token}` },
      }
    },
    handoffIsCurrent(minimumWindow = true) {
      const selected = getValidPhoneSetupHandoff({ minRemainingMs: minimumWindow ? 30000 : 0 })
      if (!selected || selected !== this.handoff) return false
      if (selected.source === SOURCE_LOGIN_MODERN) {
        return useJwt.isPhoneSetupContinuationCurrent(selected.loginIntent.identity)
      }
      if (selected.source === SOURCE_PASSWORD) {
        const tuple = useJwt.currentCredentialVersion()
        return tuple
          && String(tuple.userId) === String(selected.credential.userId)
          && String(tuple.sid) === String(selected.credential.sid)
          && tuple.ae === selected.credential.ae
          && tuple.se === selected.credential.se
      }
      return selected.source === SOURCE_LOGIN_LEGACY
    },
    exitInvalidHandoff() {
      clearPhoneSetupHandoff()
      this.handoff = null
      this.$router.replace({ name: 'auth-login' })
    },
    errorText(error) {
      const response = error && error.response
      const data = response && response.data ? response.data : {}
      if (response && response.status === 429) return this.$t(OTP_ERROR_KEYS.throttled)
      const mapped = OTP_ERROR_KEYS_BY_CODE[Number(data.errorCode)]
      if (mapped) return this.$t(mapped)
      return data.message || this.$t(OTP_ERROR_KEYS.invalidCode)
    },
    // CODE-R1-013: a factor rotation that requires a restart surfaces either
    // as the server's 503/503003, or (on a re-attempt after that state is
    // already recorded) as a synchronous CredentialStateError from useJwt's
    // armFactorPending guard, which never reaches the network.
    isFactorRestartRequired(error) {
      const response = error && error.response
      if (response && response.status === 503 && Number(response.data && response.data.errorCode) === 503003) return true
      return Boolean(error && error.name === 'CredentialStateError' && /restart/i.test(error.message || ''))
    },
    startCountdown(seconds) {
      if (this.timer) clearInterval(this.timer)
      this.cooldown = Number(seconds) || 0
      this.timer = setInterval(() => {
        if (this.cooldown > 0) this.cooldown -= 1
        if (this.cooldown <= 0) {
          clearInterval(this.timer)
          this.timer = null
        }
      }, 1000)
    },
    startExpiryCountdown(seconds) {
      if (this.expiryTimer) clearInterval(this.expiryTimer)
      this.expiresIn = Number(seconds) || 0
      this.expired = this.expiresIn <= 0
      if (this.expired) return
      this.expiryTimer = setInterval(() => {
        if (this.expiresIn > 0) this.expiresIn -= 1
        if (this.expiresIn <= 0) {
          this.expired = true
          clearInterval(this.expiryTimer)
          this.expiryTimer = null
        }
      }, 1000)
    },
    backToPhone() {
      this.step = 'phone'
      this.code = ''
      this.errorMessage = ''
      if (this.timer) clearInterval(this.timer)
      if (this.expiryTimer) clearInterval(this.expiryTimer)
    },
    async requestChallenge() {
      if (this.restartRequired) return
      if (!this.handoffIsCurrent() || !canStartPhoneSetupRequest(this.handoff)) {
        this.exitInvalidHandoff()
        return
      }
      if (this.step === 'phone' && !this.phoneValid) return
      this.loading = true
      this.errorMessage = ''
      try {
        const { data } = await axios.post(
          authRoutes.phoneSetupRequest,
          { phone: stripPhone(this.phone) },
          this.authHeaders(),
        )
        this.challengeId = data.challengeId
        this.code = data.devCode || ''
        this.step = 'code'
        this.startCountdown(data.resendCooldownSeconds)
        this.startExpiryCountdown(data.expiresInSeconds)
      } catch (error) {
        this.errorMessage = this.errorText(error)
        if (error.response && error.response.status === 401) {
          this.expireHandoff()
        } else if (!error.response && this.handoff && this.handoff.source !== SOURCE_PASSWORD) {
          // A lost/timed-out login-origin request never armed any factor
          // state; return to Login rather than leave a stale handoff that a
          // later retry or response could reuse against a superseded intent.
          this.exitInvalidHandoff()
        }
        const retry = error.response && error.response.data && error.response.data.retryAfterSeconds
        if (retry) this.startCountdown(retry)
      } finally {
        this.loading = false
      }
    },
    async verify() {
      if (this.restartRequired) return
      if (!this.handoffIsCurrent() || !this.codeValid) {
        if (this.handoff && !this.handoffIsCurrent()) this.exitInvalidHandoff()
        return
      }
      this.loading = true
      this.errorMessage = ''
      try {
        let data
        if (this.handoff.source === SOURCE_PASSWORD) {
          data = await useJwt.runFactorVerification(authRoutes.phoneSetupVerify, {
            challengeId: this.challengeId,
            code: this.code,
          }, {
            requestConfig: this.authHeaders(),
          })
        } else {
          const response = await axios.post(
            authRoutes.phoneSetupVerify,
            { challengeId: this.challengeId, code: this.code },
            this.authHeaders(),
          )
          data = response.data
        }
        await this.onVerified(data)
      } catch (error) {
        if (this.isFactorRestartRequired(error)) {
          this.restartRequired = true
          this.errorMessage = this.$t('Verification service is restarting. Please try again in a moment.')
        } else {
          this.errorMessage = this.errorText(error)
          if (error.response && error.response.status === 401) this.exitInvalidHandoff()
          if (!error.response && this.handoff && this.handoff.source !== SOURCE_PASSWORD) this.exitInvalidHandoff()
        }
      } finally {
        this.loading = false
      }
    },
    expireHandoff() {
      this.exitInvalidHandoff()
    },
    async onVerified(data) {
      if (!this.handoffIsCurrent(false)) throw new Error('Phone setup handoff was superseded')
      let acceptedData = data
      if (this.handoff.source === SOURCE_LOGIN_MODERN) {
        if (!data || !data.loginGrant) throw new Error('Phone setup did not return a login grant')
        const response = await useJwt.finalizeLogin({
          intent: this.handoff.loginIntent,
          loginGrant: data.loginGrant,
        })
        acceptedData = response.data
      } else if (this.handoff.source === SOURCE_LOGIN_LEGACY) {
        await useJwt.acceptLegacyCredentialResponse(data, this.handoff.legacyLane)
      }
      const returnTo = (this.handoff && this.handoff.returnTo) || '/'
      clearPhoneSetupHandoff()
      this.handoff = null
      if (acceptedData.user) {
        this.$router.push(returnTo).then(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: `${this.$t('Welcome')} ${acceptedData.user.displayName || acceptedData.user.name}`, icon: 'CoffeeIcon', variant: 'success' },
          })
        })
      } else {
        this.$router.push(returnTo).then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Phone verified'), text: this.$t('Please submit your change again.'), icon: 'CheckIcon', variant: 'success',
            },
          })
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
